// @ts-strict-ignore
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import { massageMonitoringData, nilTextForStatisticType, statTypeForMonitoringMetricType } from '@/helpers';
import { colorForMonitoringMetricHealthStatus } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAppIntegrations, selectMonitoringMetricsById } from '@/selectors/selectors';
import {
    CompletedReleaseSummary,
    IntegrationType,
    MonitoringMetricHealthStatus,
    SubscriptionFeatureType,
    TrendStatus,
} from '@/types';
import * as React from 'react';
import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import { flatMap } from 'lodash';
import { MonitoringStatisticGroup } from '@/typescriptHelpers';
import { arrowForTrend } from '@/components/orgOverview/OrgOverviewStatCards';
import { Link } from 'react-router-dom';
import Button from 'antd/lib/button';
import { createReleaseRolloutRoute } from '@/helpers/routeHelpers';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import RightArrowCircledIcon from '@/components/icons/RightArrowCircledIcon';
import Tooltip from 'antd/lib/tooltip';
import { lastUpdatedLabel, MetricDataNotYetAvailableText } from '@/components/monitoring/monitoringHelpers';

export interface MonitoringMetricsProps {
    orgId: string;
    appId: string;
    summary: CompletedReleaseSummary;
}

export const MonitoringMetricsWidget: React.FC<MonitoringMetricsProps> = ({ summary, appId, orgId }) => {
    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, appId));
    const integrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    const groupedMonitoringStatistics = massageMonitoringData({
        monitoringStatistics: summary.rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: summary.rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
        shouldGroupStats: false,
        integrations,
    });

    const flattenedGroups = flatMap(groupedMonitoringStatistics, (stat, index) => {
        return stat.groups.map((group) => ({
            ...(group as MonitoringStatisticGroup),
            title: stat.title,
            integrationType: stat.integrationType,
            key: `${stat.key}-${index}`,
        }));
    });

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                alignItems: 'end',
                marginBottom: 20,
                width: '100%',
            }}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: 8,
                    width: '100%',
                }}
            >
                {flattenedGroups.map((group, index) => (
                    <MonitoringMetricWidgetCard key={`${group.key}-${index}`} {...group} />
                ))}
            </div>
            {flattenedGroups.length > 0 && (
                <Link to={createReleaseRolloutRoute(orgId, appId, summary.releaseId)}>
                    <Button type="link" style={{ fontSize: 13, fontWeight: 200, paddingRight: 0 }}>
                        <div
                            style={{
                                display: 'inline-flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 8,
                                height: '100%',
                            }}
                        >
                            Rollout
                            <RightArrowCircledIcon />
                        </div>
                    </Button>
                </Link>
            )}
        </div>
    );
};

const MonitoringMetricWidgetCard: React.FC<
    MonitoringStatisticGroup & {
        integrationType: IntegrationType;
        title: string;
    }
> = ({ status, integrationType, title, value, type, change, delta, currentValueUpdatedAt, isAwaitingData }) => {
    const statusColor = colorForMonitoringMetricHealthStatus(status);

    const statType = statTypeForMonitoringMetricType(type);
    const trendStatus =
        !status ? TrendStatus.Neutral
        : status === MonitoringMetricHealthStatus.Healthy ? TrendStatus.Good
        : TrendStatus.Bad;

    const hasValue = value !== undefined && value !== null;
    const formattedValue = hasValue ? value : nilTextForStatisticType(statType);
    const lastUpdated = lastUpdatedLabel(currentValueUpdatedAt);
    // Text's own ellipsis tooltip only appears when truncated, so the timestamp needs an always-on tooltip
    const withLastUpdated = (text: React.ReactElement) =>
        lastUpdated ? <Tooltip title={lastUpdated}>{text}</Tooltip> : text;

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        borderRadius: 4,
                        border: `1px solid ${antGrey.grey5}`,
                        backgroundColor: runwayColors.white,
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        gap: 8,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: -1,
                            left: -1,
                            height: 'calc(100% + 2px)',
                            width: 7,
                            boxSizing: 'content-box',
                            borderRadius: '8px 0px 0px 8px',
                            backgroundColor: isDisabled ? antGrey.grey5 : statusColor,
                        }}
                    />
                    <IntegrationIcon integrationId={integrationType} size={16} marginTop={2} />
                    <Text
                        style={{
                            color: antGrey.grey7,
                            fontSize: 12,
                            lineHeight: 'normal',
                        }}
                        ellipsis={{
                            tooltip: title,
                        }}
                    >
                        {title}
                    </Text>
                    <div
                        style={{
                            flexGrow: 1,
                        }}
                    />

                    {isDisabled ?
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip mini />
                    : isAwaitingData ?
                        <MetricDataNotYetAvailableText fontSize={12} lineHeight="normal" />
                    :   <>
                            {!delta &&
                                withLastUpdated(
                                    <Text
                                        style={{
                                            color: hasValue ? antGrey.grey8 : antGrey.grey6,
                                            fontSize: 12,
                                            lineHeight: 'normal',
                                        }}
                                        ellipsis={{
                                            tooltip: !lastUpdated && formattedValue,
                                        }}
                                    >
                                        {formattedValue}
                                    </Text>,
                                )}
                            {arrowForTrend(change, trendStatus, 12)}
                            {delta &&
                                withLastUpdated(
                                    <Text
                                        style={{
                                            color: hasValue ? antGrey.grey8 : antGrey.grey6,
                                            fontSize: 12,
                                            lineHeight: 'normal',
                                            flexShrink: 0,
                                        }}
                                        ellipsis={{
                                            tooltip: !lastUpdated && delta,
                                        }}
                                    >
                                        {delta}
                                    </Text>,
                                )}
                        </>
                    }
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};
